import React, { useState } from 'react';
import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  TextField,
  Typography,
} from '@mui/material';

import { RuleDefinition, isRuleTag } from "@dndModels";

import API from "@utilities/smart_api";
import FlexBox from "@components/layout/FlexBox";

import { parseRuleDefinitions, ParsedRuleDefinition } from "@dndUtilities/rule_definition_import";

const api = API.getInstance();

interface Row extends ParsedRuleDefinition {
  include: boolean;
  // A definition with this name already in the source book being imported into. Those are skipped.
  exists: boolean;
}

const nameKey = (name: string) => name.trim().toLowerCase();

/**
 * Creates Rules Definitions from text pasted out of a PDF (like the SRD's Rules Definitions),
 * in the source book being worked on. Shows what it found first, so entries can be left out.
 */
export default function RuleDefinitionImporter() {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [rows, setRows] = useState<Row[] | null>(null);
  const [existing, setExisting] = useState<RuleDefinition[]>([]);
  const [progress, setProgress] = useState<{ done: number, total: number } | null>(null);
  const [result, setResult] = useState<string>("");

  // The same source book new objects go in (see applyWorkingOn).
  const target_sb = api.user && !api.user.admin ? api.default_source_book : api.working_on_sb;
  const creating = progress !== null && result === "";

  const preview = async () => {
    const all_definitions = await api.getObjects<RuleDefinition>({ data_type: "rule_definition" });
    setExisting(all_definitions);
    const in_target = new Set(all_definitions
      .filter(o => target_sb && o.source_book_ids.includes(target_sb._id))
      .map(o => nameKey(o.name)));
    setRows(parseRuleDefinitions(text).map(parsed => {
      const exists = in_target.has(nameKey(parsed.name));
      return { ...parsed, include: !exists, exists };
    }));
    setResult("");
    setProgress(null);
  };

  // Names a "See also" can point to: what's being imported, plus what's already there.
  const resolvableNames = (current_rows: Row[]) => new Set([
    ...current_rows.filter(o => o.include || o.exists).map(o => nameKey(o.name)),
    ...existing.map(o => nameKey(o.name)),
  ]);

  const create = async () => {
    if (!rows || !target_sb) return;
    const to_create = rows.filter(o => o.include);
    const resolvable = resolvableNames(rows);
    const created: { row: Row, definition: RuleDefinition }[] = [];
    let failed = 0;
    setResult("");
    setProgress({ done: 0, total: to_create.length });
    for (const row of to_create) {
      const definition = api.applyWorkingOn(new RuleDefinition());
      definition.name = row.name;
      definition.rule_tag = isRuleTag(row.tag) ? row.tag : "None";
      // If a See also name can't be linked, keep the sentence so nothing is lost.
      const all_linked = row.see_also_names.every(name => resolvable.has(nameKey(name)));
      definition.description = all_linked || row.see_also_text === ""
        ? row.description
        : `${row.description}\n\n${row.see_also_text}`;
      try {
        const res = await api.createObject("rule_definition", definition);
        if (res && res.body.id) {
          created.push({ row, definition });
        } else {
          failed++;
        }
      } catch (ex) {
        console.error(ex);
        failed++;
      }
      setProgress(prev => prev ? { ...prev, done: prev.done + 1 } : prev);
    }

    // Now that everything has an id, link the See alsos. Ones in the same book come first.
    const by_name = new Map<string, string>();
    for (const o of existing.filter(o => !o.source_book_ids.includes(target_sb._id))) {
      by_name.set(nameKey(o.name), o._id);
    }
    for (const o of existing.filter(o => o.source_book_ids.includes(target_sb._id))) {
      by_name.set(nameKey(o.name), o._id);
    }
    for (const { definition } of created) {
      by_name.set(nameKey(definition.name), definition._id);
    }
    for (const { row, definition } of created) {
      const ids = row.see_also_names
        .map(name => by_name.get(nameKey(name)))
        .filter((id): id is string => id !== undefined && id !== definition._id);
      if (ids.length > 0) {
        definition.see_also_ids = ids;
        await api.updateObject("rule_definition", definition, ["see_also_ids"]);
      }
    }

    setResult(`Created ${created.length} definition${created.length === 1 ? "" : "s"}${failed > 0 ? `, ${failed} failed` : ""}.`);
    // Lets the index page pick up the new ones.
    api.subscription_holder.dataTypeLoaded("rule_definition");
  };

  const close = () => {
    if (creating) return;
    setOpen(false);
    if (result !== "") {
      setText("");
      setRows(null);
      setProgress(null);
      setResult("");
    }
  };

  const renderRow = (row: Row, index: number, resolvable: Set<string>) => {
    const unlinked = row.see_also_names.filter(name => !resolvable.has(nameKey(name)));
    const unknown_tag = row.tag !== "" && !isRuleTag(row.tag);
    return (
      <FlexBox key={index} direction="row" style={{ alignItems: "flex-start", borderBottom: "1px solid rgba(128,128,128,0.3)", padding: "4px 0" }}>
        <Checkbox
          size="small"
          checked={row.include}
          disabled={row.exists || creating || result !== ""}
          onChange={e => setRows(prev => prev ? prev.map((o, i) => i === index ? { ...o, include: e.target.checked } : o) : prev)}
        />
        <FlexBox direction="column" style={{ flex: 1 }}>
          <span>
            <strong>{ row.name }</strong>
            { row.tag && <Chip label={row.tag} size="small" style={{ marginLeft: 8 }} color={unknown_tag ? "warning" : "default"} /> }
          </span>
          { row.exists &&
            <span className="small_label" style={{ color: "#b26a00" }}>{ `Already in ${target_sb?.name}, so it'll be skipped.` }</span>
          }
          { unknown_tag &&
            <span className="small_label" style={{ color: "#b26a00" }}>{ `[${row.tag}] isn't one of the tags, so it'll be left off.` }</span>
          }
          { row.see_also_names.length > 0 &&
            <span className="small_label">
              See also: { row.see_also_names.join(", ") }
              { unlinked.length > 0 && <span style={{ color: "#b26a00" }}>{ ` (can't find ${unlinked.join(", ")}, so the sentence stays in the description)` }</span> }
            </span>
          }
          <span className="small_label gray">
            { row.description.length > 160 ? `${row.description.substring(0, 160)}…` : row.description }
          </span>
        </FlexBox>
      </FlexBox>
    );
  };

  const renderPreview = () => {
    if (!rows) return null;
    if (rows.length === 0) {
      return <Typography variant="body2" style={{ marginTop: 8 }}>No definitions found. Try starting each heading line with #.</Typography>;
    }
    const resolvable = resolvableNames(rows);
    const included = rows.filter(o => o.include).length;
    const skipped = rows.filter(o => o.exists).length;
    return (
      <FlexBox direction="column" style={{ marginTop: 8 }}>
        <Typography variant="body2">
          Found { rows.length }: { included } will be created{ skipped > 0 ? `, ${skipped} already exist` : "" }.
        </Typography>
        { rows.map((row, i) => renderRow(row, i, resolvable)) }
      </FlexBox>
    );
  };

  const to_create = rows ? rows.filter(o => o.include).length : 0;

  return (
    <>
      <Button size="small" variant="outlined" style={{ marginLeft: 8 }} onClick={() => setOpen(true)}>
        Import from Text
      </Button>
      <Dialog open={open} onClose={close} fullWidth maxWidth="md">
        <DialogTitle>Import Rules Definitions</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Paste the Rules Definitions text. Headings are found automatically: short Title Case lines,
            optionally with a tag like [Condition]. If it gets them wrong, start each heading line with #.
          </Typography>
          { target_sb
            ? <Typography variant="body2" gutterBottom>They&apos;ll be created in <strong>{ target_sb.name }</strong>.</Typography>
            : <Typography variant="body2" gutterBottom color="error">
                { api.user && !api.user.admin ? "You need a default source book first." : "Pick the source book you're working on first." }
              </Typography>
          }
          <TextField
            multiline
            fullWidth
            minRows={8}
            maxRows={16}
            value={text}
            disabled={creating}
            onChange={e => setText(e.target.value)}
            placeholder={"Ability Check\nAn ability check is a D20 Test that...\nAttack [Action]\n..."}
          />
          <Button size="small" variant="contained" style={{ marginTop: 8 }} disabled={text.trim() === "" || creating} onClick={preview}>
            Preview
          </Button>
          { progress &&
            <FlexBox direction="column" style={{ marginTop: 8 }}>
              <LinearProgress variant="determinate" value={progress.total > 0 ? progress.done / progress.total * 100 : 100} />
              <Typography variant="body2">{ result || `Creating ${progress.done} of ${progress.total}...` }</Typography>
            </FlexBox>
          }
          { renderPreview() }
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={creating}>{ result ? "Close" : "Cancel" }</Button>
          <Button variant="contained" onClick={create} disabled={!target_sb || to_create === 0 || creating || result !== ""}>
            Create { to_create }
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
